import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ChevronLeft, ChevronRight, Diamond } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useNavigate } from "react-router-dom";

interface FeaturedArtwork {
  id: string;
  title: string;
  artist: string | null;
  image_url: string;
  estimated_value: number | null;
}

export const HeroCarousel = () => {
  const [artworks, setArtworks] = useState<FeaturedArtwork[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFeaturedArtworks = async () => {
      try {
        const { data, error } = await supabase
          .from('artworks')
          .select('id, title, artist, image_url, estimated_value')
          .eq('status', 'approved')
          .order('views', { ascending: false })
          .limit(5);

        if (error) throw error;
        setArtworks(data || []);
      } catch (error) {
        console.error('Error fetching featured artworks:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFeaturedArtworks();
  }, []);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % artworks.length);
  }, [artworks.length]);

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + artworks.length) % artworks.length);
  }, [artworks.length]);

  // Auto-advance carousel
  useEffect(() => {
    if (artworks.length <= 1) return;
    const interval = setInterval(goToNext, 7000);
    return () => clearInterval(interval);
  }, [artworks.length, goToNext]);

  if (isLoading) {
    return (
      <div className="relative h-[500px] md:h-[650px] bg-gradient-pearl animate-pulse rounded-2xl overflow-hidden ornate-corners" />
    );
  }

  if (artworks.length === 0) {
    return (
      <div className="relative h-[500px] md:h-[650px] bg-gradient-luxury rounded-2xl overflow-hidden flex items-center justify-center ornate-corners">
        <div className="text-center text-secondary-foreground/60">
          <Diamond className="h-12 w-12 mx-auto mb-4 text-primary/40" />
          <p className="text-xl font-display">Featured artworks coming soon</p>
        </div>
      </div>
    );
  }

  const currentArtwork = artworks[currentIndex];

  return (
    <div className="relative h-[500px] md:h-[650px] rounded-2xl overflow-hidden group shadow-strong">
      {/* Background images with crossfade */}
      {artworks.map((artwork, index) => (
        <div
          key={artwork.id}
          className={cn(
            "absolute inset-0 transition-all duration-1000 ease-out",
            index === currentIndex ? "opacity-100 scale-100" : "opacity-0 scale-105"
          )}
        >
          <img
            src={artwork.image_url}
            alt={artwork.title}
            className="w-full h-full object-cover"
          />
          {/* Gradient overlays */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-transparent to-transparent" />
        </div>
      ))}

      {/* Decorative frame */}
      <div className="absolute inset-4 md:inset-6 border border-secondary-foreground/10 rounded-xl pointer-events-none" />

      {/* Content overlay */}
      <div className="absolute inset-0 flex flex-col justify-end p-8 md:p-14">
        <div className="max-w-2xl">
          {/* Tags */}
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-gradient-hero text-primary-foreground text-xs font-semibold uppercase tracking-luxury rounded-full shadow-gold">
              <Diamond className="h-3 w-3" />
              Featured
            </span>
            {currentArtwork.estimated_value && (
              <span className="px-4 py-1.5 glass text-secondary-foreground text-xs font-medium rounded-full">
                Est. ${currentArtwork.estimated_value.toLocaleString()}
              </span>
            )}
          </div>
          
          {/* Title */}
          <h2 className="text-4xl md:text-6xl font-display font-medium text-secondary-foreground mb-4 leading-tight">
            {currentArtwork.title}
          </h2>
          
          {/* Artist */}
          {currentArtwork.artist && (
            <p className="text-lg md:text-xl text-secondary-foreground/70 mb-8 font-light">
              by <span className="text-primary">{currentArtwork.artist}</span>
            </p>
          )}

          {/* CTA */}
          <Button
            onClick={() => navigate(`/gallery`)}
            className="bg-secondary-foreground/10 backdrop-blur-sm text-secondary-foreground hover:bg-secondary-foreground/20 border border-secondary-foreground/20 hover:border-secondary-foreground/40 transition-all duration-500 font-medium tracking-wide"
          >
            View in Gallery
          </Button>
        </div>
      </div>

      {/* Navigation arrows */}
      {artworks.length > 1 && (
        <>
          <Button
            variant="ghost"
            size="icon"
            onClick={goToPrev}
            className="absolute left-6 top-1/2 -translate-y-1/2 h-14 w-14 rounded-full glass text-secondary-foreground hover:bg-secondary-foreground/10 opacity-0 group-hover:opacity-100 transition-all duration-500"
          >
            <ChevronLeft className="h-6 w-6" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={goToNext}
            className="absolute right-6 top-1/2 -translate-y-1/2 h-14 w-14 rounded-full glass text-secondary-foreground hover:bg-secondary-foreground/10 opacity-0 group-hover:opacity-100 transition-all duration-500"
          >
            <ChevronRight className="h-6 w-6" />
          </Button>
        </>
      )}

      {/* Progress indicator */}
      {artworks.length > 1 && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3">
          {artworks.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={cn(
                "h-1 rounded-full transition-all duration-500",
                index === currentIndex
                  ? "bg-gradient-gold w-10"
                  : "bg-secondary-foreground/30 w-4 hover:bg-secondary-foreground/50"
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default HeroCarousel;